import { Heading, Text, Box } from "@chakra-ui/react";

export const getThemeColors = (theme: string) => {
  const base = (theme || "gray").toLowerCase();

  return {
    badgeBgLight: `${base}.200`,
    badgeBgDark: `${base}.700`,
    badgeTextLight: `${base}.800`,
    badgeTextDark: "white",

    borderLight: `${base}.300`,
    borderDark: `${base}.500`,

    accentLight: `${base}.50`,
    accentDark: `${base}.900`,

    headerBgLight: `${base}.600`,
    headerBgDark: `${base}.500`,

    standardBorderLight: "gray.200",
    standardBorderDark: "gray.700",
  };
};

/**
 * Offer promotion tiers control VISUAL PROMINENCE only.
 *
 * Typography is identical across STANDARD, FEATURED and HERO.
 *
 * STANDARD  = restrained visual treatment
 * FEATURED  = elevated visual treatment
 * HERO      = strongest visual treatment
 */
export const getOfferTierStyles = (
  promotionTier?: string,
  colors?: ReturnType<typeof getThemeColors>
) => {
  const tier = (promotionTier || "STANDARD").toUpperCase();
  const themeColors = colors || getThemeColors("gray");

  // Same typography across ALL tiers.
  const typography = {
    titleSize: "lg",
    titleWeight: "800",
    priceSize: "2xl",
    priceWeight: "800",
    descriptionSize: "md",
  };

  if (tier === "HERO") {
    return {
      ...typography,

      borderWidth: "3px",
      borderColorLight: themeColors.borderLight,
      borderColorDark: themeColors.borderDark,

      cardBgLight: themeColors.accentLight,
      cardBgDark: themeColors.accentDark,

      boxShadow: "2xl",

      topBarBgLight: themeColors.headerBgLight,
      topBarBgDark: themeColors.headerBgDark,

      padding: 7,

      hoverShadow: "dark-lg",
      hoverTransform: "translateY(-8px)",

      buttonSize: "lg",
    };
  }

  if (tier === "FEATURED") {
    return {
      ...typography,

      borderWidth: "2px",
      borderColorLight: themeColors.borderLight,
      borderColorDark: themeColors.borderDark,

      cardBgLight: "white",
      cardBgDark: "gray.900",

      boxShadow: "xl",

      topBarBgLight: themeColors.borderLight,
      topBarBgDark: themeColors.borderDark,

      padding: 6,

      hoverShadow: "2xl",
      hoverTransform: "translateY(-7px)",

      buttonSize: "lg",
    };
  }

  return {
    ...typography,

    // Same physical card geometry as FEATURED.
    borderWidth: "2px",
    borderColorLight: themeColors.standardBorderLight,
    borderColorDark: themeColors.standardBorderDark,

    // Standard retains its neutral/grey treatment.
    cardBgLight: "white",
    cardBgDark: "gray.900",

    boxShadow: "sm",

    topBarBgLight: "gray.200",
    topBarBgDark: "gray.700",

    // Same physical card padding as FEATURED.
    padding: 6,

    hoverShadow: "lg",
    hoverTransform: "translateY(-5px)",

    buttonSize: "lg",
  };
};

export const offerMarkdownComponents = {
  h1: ({ children }: any) => (
    <Heading
      as="h1"
      size="sm"
      lineHeight="1.3"
      m={0}
      mb={2}
      fontFamily="heading"
    >
      {children}
    </Heading>
  ),

  h2: ({ children }: any) => (
    <Heading
      as="h2"
      size="sm"
      fontWeight="semibold"
      lineHeight="1.3"
      m={0}
      mb={2}
      fontFamily="heading"
    >
      {children}
    </Heading>
  ),

  h3: ({ children }: any) => (
    <Heading
      as="h3"
      size="xs"
      fontWeight="semibold"
      lineHeight="1.3"
      m={0}
      mb={2}
      fontFamily="heading"
    >
      {children}
    </Heading>
  ),

  p: ({ children }: any) => (
    <Text
      fontSize="md"
      lineHeight="1.6"
      m={0}
      mb={2}
      fontFamily="body"
    >
      {children}
    </Text>
  ),

  ul: ({ children }: any) => (
    <Box
      as="ul"
      pl={4}
      m={0}
      mb={2}
      fontSize="md"
    >
      {children}
    </Box>
  ),

  li: ({ children }: any) => (
    <Box
      as="li"
      fontSize="md"
      lineHeight="1.6"
      m={0}
      mb={1}
      fontFamily="body"
    >
      {children}
    </Box>
  ),

  strong: ({ children }: any) => (
    <Text
      as="span"
      fontWeight="bold"
    >
      {children}
    </Text>
  ),
};

